import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type {
  ActionSetDto,
  ActionSetInput,
  ConditionDto,
  EditActionDto,
  MatchSetDto,
} from "../../../api/types";
import { errorText } from "../../../api/common";
import { DialogTitleWithClose } from "../../../components/DialogTitleWithClose";
import {
  ActionListEditor,
  ConditionEditor,
  joinLabels,
  retiredActionLabels,
  retiredConditionLabels,
  unsetActionLabels,
  unsetConditionLabels,
} from "../../../components/RuleEditor";
import { createDefaultCondition } from "../../../logic/auto-transaction-form-logic";
import { usageCount } from "../../../logic/library-usage";
import { ActionSetInputsEditor } from "./ActionSetInputsEditor";

/**
 * Create/edit dialog for one library entry. A match set edits a `ConditionDto`
 * and an action set edits an `EditActionDto[]`, using the same editors the rule
 * form uses — an entry is exactly the condition (or actions) half of a rule, so
 * anything expressible in a rule is expressible in a set.
 */
export type LibrarySetFormProps = {
  open: boolean;
  onClose: () => void;
  /** Which half of a rule this entry holds. */
  kind: "match" | "action";
  /** Absent when creating. */
  existing?: MatchSetDto | ActionSetDto;
  onSave: (values: {
    name: string;
    description: string | null;
    condition: ConditionDto;
    inputs: ActionSetInput[];
    actions: EditActionDto[];
  }) => Promise<unknown>;
};

const isMatchSet = (set: MatchSetDto | ActionSetDto): set is MatchSetDto =>
  "condition" in set;

export const LibrarySetForm = ({
  open,
  onClose,
  kind,
  existing,
  onSave,
}: LibrarySetFormProps) => {
  const [name, setName] = useState(existing?.name ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [condition, setCondition] = useState<ConditionDto>(
    existing && isMatchSet(existing)
      ? existing.condition
      : createDefaultCondition(),
  );
  const [actions, setActions] = useState<EditActionDto[]>(
    existing && !isMatchSet(existing) ? existing.actions : [],
  );
  const [inputs, setInputs] = useState<ActionSetInput[]>(
    existing && !isMatchSet(existing) ? existing.inputs : [],
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    const unset =
      kind === "action"
        ? unsetActionLabels(actions)
        : unsetConditionLabels(condition);
    if (unset.length > 0) {
      setError(`Pick a value for ${joinLabels(unset)} before saving.`);
      return;
    }
    const retired =
      kind === "action"
        ? retiredActionLabels(actions)
        : retiredConditionLabels(condition);
    if (retired.length > 0) {
      setError(
        `Remove ${joinLabels(retired)} before saving — the field no longer exists.`,
      );
      return;
    }

    if (kind === "action" && inputs.some((i) => i.name.trim() === "")) {
      setError("Give every input a name, or remove it.");
      return;
    }

    setError(null);
    setSaving(true);
    try {
      await onSave({
        name,
        description: description || null,
        condition,
        inputs: inputs.map((i) => ({ ...i, name: i.name.trim() })),
        actions,
      });
      onClose();
    } catch (err) {
      // The backend refuses a body that can reach itself; that message is the
      // useful one, so surface it rather than a generic failure.
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const title = `${existing ? "Edit" : "New"} ${
    kind === "match" ? "match set" : "action set"
  }`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitleWithClose onClose={onClose}>{title}</DialogTitleWithClose>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              helperText="What this entry is for — shown wherever a rule cites it."
            />

            {kind === "match" ? (
              <ConditionEditor
                condition={condition}
                onChange={setCondition}
                isMobile={isMobile}
              />
            ) : (
              <Stack spacing={2}>
                <ActionSetInputsEditor
                  inputs={inputs}
                  onChange={setInputs}
                  actions={actions}
                  usedBy={
                    existing && !isMatchSet(existing)
                      ? usageCount(existing.usedBy)
                      : 0
                  }
                />
                <ActionListEditor
                  actions={actions}
                  onChange={setActions}
                  isMobile={isMobile}
                  templateInputs={inputs.map((i) => i.name)}
                />
              </Stack>
            )}

            <Stack
              direction="row"
              spacing={2}
              sx={{ justifyContent: "flex-end" }}
            >
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};
